'use client'

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@planner/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@planner/ui'
import { Input } from '@planner/ui'
import { Label } from '@planner/ui'
import { useMenuCategories } from '@/features/menu/hooks/useMenuCategories'
import type { MenuPackageCategory, MenuCategory } from '@planner/types'
import { Plus, X, GripVertical } from 'lucide-react'
import { PackageItemManager } from './PackageItemManager'

interface PackageCategoryManagerProps {
  packageId: string
}

export function PackageCategoryManager({ packageId }: PackageCategoryManagerProps) {
  const { categories: allCategories, loading: categoriesLoading, createCategory, fetchCategories } = useMenuCategories()
  const [packageCategories, setPackageCategories] = useState<Array<MenuPackageCategory & { menu_category?: MenuCategory }>>([])
  const [loading, setLoading] = useState(true)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false)
  const [showCreateCategoryForm, setShowCreateCategoryForm] = useState(false)
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    display_order: '',
  })
  const [creatingCategory, setCreatingCategory] = useState(false)

  useEffect(() => {
    if (packageId) {
      fetchPackageCategories()
    }
  }, [packageId])

  const fetchPackageCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_package_categories')
        .select(`
          *,
          menu_category:menu_categories (*)
        `)
        .eq('menu_package_id', packageId)
        .order('display_order', { ascending: true })

      if (error) throw error
      setPackageCategories(data || [])
    } catch (error) {
      console.error('Error fetching package categories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddCategoryClick = () => {
    // Filter packageCategories to only include those with accessible menu_category
    // (RLS may block categories from other companies, resulting in null menu_category)
    const accessiblePackageCategories = packageCategories.filter(
      (pc) => pc.menu_category && pc.menu_category_id
    )

    const availableCategories = allCategories.filter(
      (cat) => !accessiblePackageCategories.some((pc) => pc.menu_category_id === cat.id)
    )

    // If no categories exist or all are already added, show create category form
    if (allCategories.length === 0 || availableCategories.length === 0) {
      setCategoryFormData({ name: '', description: '', display_order: '' })
      setShowCreateCategoryForm(true)
      setShowAddCategoryDialog(false)
      return
    }

    // Otherwise show dialog to select existing category
    setSelectedCategoryId('')
    setShowAddCategoryDialog(true)
    setShowCreateCategoryForm(false)
  }

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!packageId) return

    setCreatingCategory(true)
    try {
      const displayOrder = categoryFormData.display_order 
        ? parseInt(categoryFormData.display_order) 
        : undefined

      const result = await createCategory(
        categoryFormData.name,
        categoryFormData.description || undefined,
        displayOrder
      )

      if (result.error) {
        alert(result.error)
        setCreatingCategory(false)
        return
      }

      if (result.data) {
        // Automatically add the newly created category to the package
        const maxOrder = packageCategories.length > 0
          ? Math.max(...packageCategories.map((pc) => pc.display_order))
          : 0

        const { error: addError } = await supabase
          .from('menu_package_categories')
          .insert([{
            menu_package_id: packageId,
            menu_category_id: result.data.id,
            min_selections: 1,
            max_selections: 1,
            display_order: maxOrder + 1,
          }] as unknown as never)

        if (addError) throw addError

        // Refresh categories and package categories
        await fetchCategories()
        await fetchPackageCategories()
        setShowCreateCategoryForm(false)
        setCategoryFormData({ name: '', description: '', display_order: '' })
      }
    } catch (error) {
      console.error('Error creating category:', error)
      alert('Błąd podczas tworzenia kategorii')
    } finally {
      setCreatingCategory(false)
    }
  }

  const addCategory = async () => {
    if (!packageId || !selectedCategoryId) return

    try {
      const maxOrder = packageCategories.length > 0
        ? Math.max(...packageCategories.map((pc) => pc.display_order))
        : 0

      const { error } = await supabase
        .from('menu_package_categories')
        .insert([{
          menu_package_id: packageId,
          menu_category_id: selectedCategoryId,
          min_selections: 1,
          max_selections: 1,
          display_order: maxOrder + 1,
        }] as unknown as never)
        .select(`
          *,
          menu_category:menu_categories (*)
        `)
        .single()

      if (error) throw error
      await fetchPackageCategories()
      setShowAddCategoryDialog(false)
      setSelectedCategoryId('')
    } catch (error) {
      console.error('Error adding category:', error)
      alert('Błąd podczas dodawania kategorii')
    }
  }

  const removeCategory = async (categoryId: string) => {
    if (!confirm('Czy na pewno chcesz usunąć tę kategorię z pakietu?')) return

    try {
      const { error } = await supabase
        .from('menu_package_categories')
        .delete()
        .eq('id', categoryId)

      if (error) throw error
      await fetchPackageCategories()
    } catch (error) {
      console.error('Error removing category:', error)
      alert('Błąd podczas usuwania kategorii')
    }
  }

  const updateCategory = async (categoryId: string, updates: Partial<MenuPackageCategory>) => {
    try {
      const { error } = await supabase
        .from('menu_package_categories')
        .update(updates as unknown as never)
        .eq('id', categoryId)

      if (error) throw error
      await fetchPackageCategories()
    } catch (error) {
      console.error('Error updating category:', error)
      alert('Błąd podczas aktualizacji kategorii')
    }
  }

  if (loading || categoriesLoading) {
    return <div className="p-4">Ładowanie...</div>
  }

  // Filter packageCategories to only include those with accessible menu_category
  // (RLS may block categories from other companies, resulting in null menu_category)
  const accessiblePackageCategories = packageCategories.filter(
    (pc) => pc.menu_category && pc.menu_category_id
  )

  const availableCategories = allCategories.filter(
    (cat) => !accessiblePackageCategories.some((pc) => pc.menu_category_id === cat.id)
  )

  return (
    <div className="space-y-4 pb-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Kategorie w pakiecie</h3>
        <Button type="button" onClick={handleAddCategoryClick} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Dodaj kategorię
        </Button>
      </div>

      {showCreateCategoryForm && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle>Utwórz nową kategorię</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category-name">Nazwa *</Label>
                <Input
                  id="category-name"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category-description">Opis</Label>
                <textarea
                  id="category-description"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category-display-order">Kolejność wyświetlania</Label>
                <Input
                  id="category-display-order"
                  type="number"
                  min="0"
                  value={categoryFormData.display_order}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, display_order: e.target.value })}
                  placeholder="Auto"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateCategoryForm(false)
                    setCategoryFormData({ name: '', description: '', display_order: '' })
                  }}
                  disabled={creatingCategory}
                >
                  Anuluj
                </Button>
                <Button
                  type="submit"
                  disabled={creatingCategory || !categoryFormData.name.trim()}
                >
                  {creatingCategory ? 'Tworzenie...' : 'Utwórz i dodaj do pakietu'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {showAddCategoryDialog && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle>Wybierz kategorię do dodania</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="category-select">Kategoria</Label>
              <select
                id="category-select"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <option value="">-- Wybierz kategorię --</option>
                {availableCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddCategoryDialog(false)
                  setSelectedCategoryId('')
                }}
              >
                Anuluj
              </Button>
              <Button
                type="button"
                onClick={addCategory}
                disabled={!selectedCategoryId}
              >
                Dodaj
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {packageCategories.length === 0 ? (
        <Card>
          <CardContent className="p-4 text-center text-muted-foreground">
            Brak kategorii. Dodaj pierwszą kategorię.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 mb-6">
          {packageCategories.map((pkgCat) => {
            const categoryName = pkgCat.menu_category?.name || 'Nieznana kategoria'
            const isExpanded = expandedCategory === pkgCat.id

            return (
              <Card key={pkgCat.id}>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-muted-foreground" />
                      <CardTitle className="text-base">{categoryName}</CardTitle>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedCategory(isExpanded ? null : pkgCat.id)}
                      >
                        {isExpanded ? 'Zwiń' : 'Rozwiń'}
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeCategory(pkgCat.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`min-${pkgCat.id}`}>Min. liczba dań</Label>
                      <Input
                        id={`min-${pkgCat.id}`}
                        type="number"
                        min="0"
                        value={pkgCat.min_selections}
                        onChange={(e) =>
                          updateCategory(pkgCat.id, {
                            min_selections: parseInt(e.target.value) || 0,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor={`max-${pkgCat.id}`}>Maks. liczba dań</Label>
                      <Input
                        id={`max-${pkgCat.id}`}
                        type="number"
                        min={pkgCat.min_selections}
                        value={pkgCat.max_selections}
                        onChange={(e) =>
                          updateCategory(pkgCat.id, {
                            max_selections: parseInt(e.target.value) || pkgCat.min_selections,
                          })
                        }
                      />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="pt-4 border-t">
                      <PackageItemManager
                        packageCategoryId={pkgCat.id}
                        categoryName={categoryName}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

